import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from './SEO';
import { getPostBySlug, blogPosts } from '../data/blog/index';
import '../styles/BlogPost.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    // Buscar el post por slug
    const foundPost = getPostBySlug(slug);
    
    if (!foundPost) {
      // Si no existe, redirigir a 404
      navigate('/404');
      return;
    }

    setPost(foundPost);

    // Encontrar posts relacionados (misma categoría o tags compartidos)
    const related = blogPosts
      .filter(p => 
        p.id !== foundPost.id && 
        (p.category === foundPost.category || 
         p.tags.some(tag => foundPost.tags.includes(tag)))
      )
      .slice(0, 3);
    
    setRelatedPosts(related);

    // Scroll al inicio
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = post.title;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  if (!post) {
    return (
      <div className="blog-post-loading">
        <div className="spinner"></div>
        <p>Cargando artículo...</p>
      </div>
    );
  }

  const seoData = {
    title: post.seo.metaTitle || post.title,
    description: post.seo.metaDescription || post.description,
    keywords: post.seo.keywords,
    canonical: `/blog/${post.slug}`,
    ogTitle: post.seo.metaTitle || post.title,
    ogDescription: post.seo.metaDescription || post.description,
    ogImage: post.seo.ogImage || post.image,
    ogType: 'article',
    schema: [
      // Article Schema
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "alternativeHeadline": post.seo.metaTitle || post.title,
        "description": post.description,
        "image": {
          "@type": "ImageObject",
          "url": `https://runamatic.io${post.image}`,
          "width": 1200,
          "height": 630
        },
        "datePublished": post.date,
        "dateModified": post.date,
        "author": {
          "@type": "Organization",
          "name": post.author,
          "url": "https://runamatic.io"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Runamatic",
          "logo": {
            "@type": "ImageObject",
            "url": "https://runamatic.io/LogoNegro.png",
            "width": 200,
            "height": 60
          },
          "url": "https://runamatic.io"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://runamatic.io/blog/${post.slug}`
        },
        "keywords": post.tags.join(', '),
        "articleSection": post.category,
        "wordCount": post.content.split(' ').length,
        "timeRequired": post.readTime,
        "inLanguage": "es-ES"
      },
      // Breadcrumb Schema
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": "https://runamatic.io"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://runamatic.io/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": post.title,
            "item": `https://runamatic.io/blog/${post.slug}`
          }
        ]
      },
      // Organization Schema
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Runamatic",
        "url": "https://runamatic.io",
        "logo": "https://runamatic.io/LogoNegro.png",
        "sameAs": [
          "https://www.linkedin.com/company/103662838",
          "https://www.instagram.com/runamatic/",
          "https://www.facebook.com/profile.php?id=61559663574965",
          "https://www.youtube.com/@Runamatic"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "hola@runamatic.io",
          "availableLanguage": ["Spanish", "English"]
        }
      }
    ]
  };

  return (
    <>
      <SEO {...seoData} />
      <div className="blog-post-page">
        {/* Breadcrumb */}
        <div className="blog-post-breadcrumb">
          <div className="blog-post-container">
            <Link to="/" className="breadcrumb-link">Inicio</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/blog" className="breadcrumb-link">Blog</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{post.title}</span>
          </div>
        </div>

        {/* Header */}
        <article className="blog-post-article">
          <motion.header
            className="blog-post-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="blog-post-container">
              <div className="blog-post-header-meta">
                <span className="blog-post-category">
                  <i className="fas fa-folder"></i>
                  {post.category}
                </span>
                <span className="blog-post-read-time">
                  <i className="far fa-clock"></i>
                  {post.readTime}
                </span>
              </div>

              <h1 className="blog-post-main-title">{post.title}</h1>
              
              <p className="blog-post-main-description">{post.description}</p>

              <div className="blog-post-meta-row">
                <div className="blog-post-author-info">
                  <div className="author-avatar">
                    <i className="fas fa-user-circle"></i>
                  </div>
                  <div className="author-details">
                    <span className="author-name">{post.author}</span>
                    <span className="post-date">
                      {new Date(post.date).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>

                <div className="blog-post-share">
                  <button
                    className="share-toggle-btn"
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    aria-label="Compartir artículo"
                  >
                    <i className="fas fa-share-alt"></i>
                    Compartir
                  </button>

                  {showShareMenu && (
                    <motion.div
                      className="share-menu"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <button
                        onClick={() => handleShare('twitter')}
                        className="share-btn twitter"
                        aria-label="Compartir en Twitter"
                      >
                        <i className="fab fa-twitter"></i>
                      </button>
                      <button
                        onClick={() => handleShare('facebook')}
                        className="share-btn facebook"
                        aria-label="Compartir en Facebook"
                      >
                        <i className="fab fa-facebook-f"></i>
                      </button>
                      <button
                        onClick={() => handleShare('linkedin')}
                        className="share-btn linkedin"
                        aria-label="Compartir en LinkedIn"
                      >
                        <i className="fab fa-linkedin-in"></i>
                      </button>
                      <button
                        onClick={() => handleShare('whatsapp')}
                        className="share-btn whatsapp"
                        aria-label="Compartir en WhatsApp"
                      >
                        <i className="fab fa-whatsapp"></i>
                      </button>
                      <button
                        onClick={handleCopyLink}
                        className={`share-btn copy ${copySuccess ? 'success' : ''}`}
                        aria-label="Copiar enlace"
                      >
                        <i className={copySuccess ? 'fas fa-check' : 'fas fa-link'}></i>
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>

              <div className="blog-post-tags-header">
                {post.tags.map(tag => (
                  <span key={tag} className="blog-post-tag">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </motion.header>

          {/* Content */}
          <motion.div
            className="blog-post-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="blog-post-container">
              <div className="blog-post-body">
                {/* eslint-disable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */}
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h2 className="content-h1" {...props} />,
                    h2: ({node, ...props}) => <h3 className="content-h2" {...props} />,
                    h3: ({node, ...props}) => <h4 className="content-h3" {...props} />,
                    h4: ({node, ...props}) => <h5 className="content-h4" {...props} />,
                    p: ({node, ...props}) => <p className="content-paragraph" {...props} />,
                    ul: ({node, ...props}) => <ul className="content-list" {...props} />,
                    ol: ({node, ...props}) => <ol className="content-list ordered" {...props} />,
                    li: ({node, ...props}) => <li className="content-list-item" {...props} />,
                    a: ({node, ...props}) => <a className="content-link" target="_blank" rel="noopener noreferrer" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="content-blockquote" {...props} />,
                    code: ({node, inline, ...props}) => 
                      inline ? 
                        <code className="content-code-inline" {...props} /> : 
                        <code className="content-code-block" {...props} />,
                    strong: ({node, ...props}) => <strong className="content-strong" {...props} />,
                    em: ({node, ...props}) => <em className="content-em" {...props} />,
                    table: ({node, ...props}) => (
                      <div className="table-wrapper">
                        <table className="content-table" {...props} />
                      </div>
                    ),
                    thead: ({node, ...props}) => <thead className="content-table-head" {...props} />,
                    tbody: ({node, ...props}) => <tbody className="content-table-body" {...props} />,
                    tr: ({node, ...props}) => <tr className="content-table-row" {...props} />,
                    th: ({node, ...props}) => <th className="content-table-header" {...props} />,
                    td: ({node, ...props}) => <td className="content-table-cell" {...props} />,
                  }}
                >
                  {post.content}
                </ReactMarkdown>
                {/* eslint-enable jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content */}
              </div>
            </div>
          </motion.div>

          {/* Footer CTA */}
          <motion.div
            className="blog-post-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="blog-post-container">
              <div className="blog-post-cta-content">
                <h2>¿Te gustaría implementar automatización en tu negocio?</h2>
                <p>Agenda una demo personalizada y descubre cómo Runamatic puede ayudarte</p>
                <div className="blog-post-cta-buttons">
                  <Link to="/#demo" className="blog-post-cta-btn primary">
                    Solicitar demo gratuita
                    <i className="fas fa-arrow-right"></i>
                  </Link>
                  <Link to="/#plans" className="blog-post-cta-btn secondary">
                    Ver planes y precios
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="blog-related-posts">
            <div className="blog-post-container">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="related-posts-title">Artículos relacionados</h2>
                <div className="related-posts-grid">
                  {relatedPosts.map((relatedPost, index) => (
                    <motion.article
                      key={relatedPost.id}
                      className="related-post-card"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ y: -5 }}
                    >
                      <Link to={`/blog/${relatedPost.slug}`} className="related-post-link">
                        <div className="related-post-image">
                          <h3 className="related-post-image-title">{relatedPost.title}</h3>
                        </div>
                        <div className="related-post-content">
                          <span className="related-post-category">{relatedPost.category}</span>
                          <p className="related-post-description">{relatedPost.description}</p>
                          <div className="related-post-meta">
                            <span className="related-post-date">
                              <i className="far fa-calendar"></i>
                              {new Date(relatedPost.date).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                            <span className="related-post-read-time">
                              <i className="far fa-clock"></i>
                              {relatedPost.readTime}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>
              </motion.div>

              <motion.div
                className="back-to-blog"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <Link to="/blog" className="back-to-blog-btn">
                  <i className="fas fa-arrow-left"></i>
                  Ver todos los artículos
                </Link>
              </motion.div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default BlogPostPage;
